import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { GlassRegular } from './GlassSurface/index.ts';
import { useMotionConfig } from '../../hooks/useMotionConfig.ts';
import { Icon } from '../../components/common/Icon.tsx';

const NativeButton = 'button';
const modalSurfaceStyle = {
    backgroundColor: 'var(--modal-surface)',
} as React.CSSProperties;

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    children: React.ReactNode;
    size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full' | 'responsive';
    headless?: boolean;
    surface?: 'glass' | 'solid';
}

export const Modal: React.FC<ModalProps> = ({ 
    isOpen, 
    onClose, 
    title, 
    children, 
    size = 'md',
    headless = false,
    surface = 'glass'
}) => {
    const motionConfig = useMotionConfig();

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape, { passive: true });
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    const sizeClasses = {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-lg',
        xl: 'max-w-xl',
        '2xl': 'max-w-2xl',
        '3xl': 'max-w-3xl',
        'full': 'max-w-[95vw] max-h-[95vh]',
        responsive: 'max-w-[min(96vw,960px)] max-h-[calc(100vh-2rem)]'
    };

    return createPortal(
        <AnimatePresence>
            {isOpen ? <motion.div
                    className="modal-root fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={motionConfig.subtle}
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.95, opacity: 0 }}
                        transition={motionConfig.overlay}
                        className={`w-full ${sizeClasses[size]}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <GlassRegular 
                            className={"flex max-h-[calc(100vh-2rem)] w-full flex-col shadow-2xl"}
                            style={surface === 'solid' ? modalSurfaceStyle : undefined}
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby={title ? "modal-title" : undefined}
                        >
                            {!headless && title ? <div className="flex shrink-0 items-center justify-between border-gray-100 border-b px-6 pt-6 pb-4 dark:border-border-dark">
                                    <h3 id="modal-title" className="font-bold text-lg text-text-light dark:text-text-dark">
                                        {title}
                                    </h3>
                                    <NativeButton
                                        type="button"
                                        onClick={onClose}
                                        className="focus-ring flex min-h-[44px] min-w-[44px] items-center justify-center rounded text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-white/10 dark:hover:text-gray-200"
                                        aria-label="Close modal"
                                    >
                                        <Icon name="close" />
                                    </NativeButton>
                                </div> : null}
                            <div className={`min-h-0 flex-1 overflow-y-auto ${headless ? '' : 'p-6'}`}>
                                {children}
                            </div>
                        </GlassRegular>
                    </motion.div>
                </motion.div> : null}
        </AnimatePresence>,
        document.body
    );
};


// Subcomponents for structured layout
export const ModalHeader: React.FC<{ 
    title: string; 
    onClose?: () => void;
    icon?: string;
}> = ({ title, onClose, icon }) => (
    <div className="mb-4 flex items-center justify-between border-gray-100 border-b pb-2 dark:border-border-dark">
        <h3 className="flex items-center gap-2 font-bold text-lg text-text-light dark:text-text-dark">
            {icon ? <Icon name={icon} className="text-primary" /> : null}
            {title}
        </h3>
        {onClose ? <NativeButton
                type="button"
                onClick={onClose}
                className="focus-ring flex min-h-[44px] min-w-[44px] items-center justify-center rounded text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-white/10 dark:hover:text-gray-200"
                aria-label="Close modal"
            >
                <Icon name="close" />
            </NativeButton> : null}
    </div>
);

export const ModalFooter: React.FC<{
    children: React.ReactNode;
    className?: string; // Allow custom styling if really needed
}> = ({ children, className = '' }) => (
    <div className={`mt-6 flex items-center justify-end gap-3 border-gray-100 border-t pt-4 dark:border-border-dark ${className}`}>
        {children}
    </div>
);








